import { useState, useCallback } from 'react';
import type { ChatMessage } from '../context/ChatContext';

// Define the shape of the props this hook expects
interface UseDEAIChatProps {
  onSendText: (text: string) => void;
  onSendAudio: (blob: Blob) => void;
}

export const useDEAIChat = ({ onSendText, onSendAudio }: UseDEAIChatProps) => {
  const [messageInput, setMessageInput] = useState("");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  const addMessage = useCallback((newMessage: ChatMessage) => {
    setChatMessages(prevMessages => {
      // FIX: Instead of finding by ID, we check the last message in the array.
      const lastMessage = prevMessages[prevMessages.length - 1];

      // If the new chunk is from the assistant AND the last message is also from the assistant,
      // append the content to the last message.
      if (newMessage.role === 'assistant' && lastMessage?.role === 'assistant') {
        const updatedMessages = [...prevMessages];
        // Use the original ID of the last message for stability.
        updatedMessages[prevMessages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + newMessage.content,
        };
        return updatedMessages;
      }
      
      // Otherwise, it's a new message (either from the user or the first chunk from the assistant).
      return [...prevMessages, newMessage];
    });
  }, []);

  const clearMessages = useCallback(() => setChatMessages([]), []);

  const sendTextMessage = useCallback(() => {
    const content = messageInput.trim();
    if (!content) return;

    const userMessage: ChatMessage = { id: `${Date.now()}`, role: "user", content };
    
    setChatMessages((prev) => [...prev, userMessage]);
    setMessageInput("");

    if (onSendText) {
      onSendText(content);
    }
  }, [messageInput, onSendText]);

  const sendAudioMessage = useCallback((audioBlob: Blob) => {
    if (onSendAudio) {
      onSendAudio(audioBlob);
    }
  }, [onSendAudio]);

  return {
    chatMessages,
    messageInput,
    setMessageInput,
    sendTextMessage,
    sendAudioMessage,
    addMessage,
    clearMessages,
  };
};